import React from "react";
import { Breadcrumb } from "../../../../atoms/breadcrumb";
import { Input } from "../../../../atoms/input";
import { pascalToSnakeCase } from "../../../../../utils";
import { PAGE_NAME, ROUTE } from "../../../../../constants/constants";
import { Dropdown } from "../../../../atoms/dropdown";
import { Divider } from "../../../../atoms/divider";
import { Table } from "../../../../atoms/table";
import { Pagination } from "../../../../molecules/pagination";
import { usePaginationHook } from "../../../../molecules/pagination/usePaginationHook";
import { useTableHooks } from "../../../../atoms/table/useTableHooks";
import { useApprovedRequestListQuery } from "../../../../../store/requestManagement/requestSlice";
import { useActiveUserListQuery } from "../../../../../store/user/userSlice";
import { credentialsManagementOptions, requestTypeOptions, subTypeOptionsAssignLicense, subTypeOptionsPermission, subTypeOptionsProjectTeam, subTypeOptionsSaaS, subTypeOptionsUserLicense, subTypeOtherOptions, subTypeUserProfileOptions } from "../../../requestManagement/myRequest/create";
import { MultipleDropdown } from "../../../../atoms/multipleDropdown";
import { Icon } from "../../../../atoms/icon";

const toolSortHandler = (sortBy: string) => {
    if (sortBy === "createdAtTime") {
        sortBy = "createdAt";
    } else if (sortBy === "requestStatus") {
        sortBy = "status";
    } else if (sortBy === "requestId") {
        sortBy = "id";
    } else if (sortBy === "requestAssignee") {
        sortBy = "assignee";
    }

    return sortBy;
};

const ApprovedRequestList = () => {
    const pageClassName = "p-approvedRequestList";

    const ref = React.useRef<HTMLDivElement>(null);
    const filterRef = React.useRef<HTMLDivElement>(null);
    const [isShowFilter, setShowFilter] = React.useState(false);
    const [filter, setFilter] = React.useState({
        type: {
            label: "Select Type",
            value: "",
        },
        subType: {
            label: "Select Sub Type",
            value: "",
        },
        assignees: [] as { label: string; value: string }[],
    });

    const [filterQuery, setFilterQuery] = React.useState({
        type: "",
        subType: "",
        assignees: "",
    });

    const {
        search,
        query,
        sortHandler,
        searchHandler,
        limitChangeHandler,
        pageChangeHandler,
    } = useTableHooks();

    const { data, isLoading, isFetching } = useApprovedRequestListQuery({
        search: query.search,
        sort_by: pascalToSnakeCase(toolSortHandler(query.sortBy)),
        sort_direction: query.sortDirection,
        limit: query.limit,
        current_page: query.current,
        type: filterQuery.type,
        sub_type: filterQuery.subType,
        assignees: filterQuery.assignees,
    });

    const { data: userListData, isLoading: isUserListLoading } = useActiveUserListQuery(
        {}
    );


    const tableHeader = [
        {
            title: "Serial",
            dataIndex: "id",
            isSortable: true,
        },
        {
            title: "ID",
            dataIndex: "requestId",
            isSortable: true,
        },
        {
            title: "Requester",
            dataIndex: "requester",
            isSortable: true,
        },
        {
            title: "Type",
            dataIndex: "type",
            isSortable: true,
        },
        {
            title: "Sub Type",
            dataIndex: "subType",
            isSortable: true,
        },
        {
            title: "Status",
            dataIndex: "requestStatus",
            isSortable: true,
        },
        {
            title: "Assignee",
            dataIndex: "requestAssignee",
            isSortable: true,
        },
        {
            title: "Number of Request",
            dataIndex: "numberOfRequests",
            isSortable: true,
        },
        {
            title: "Created At",
            dataIndex: "createdAtTime",
            isSortable: true,
        },
    ];

    const {
        totalData,
        handleFirstPage,
        handlePrevPage,
        handleLastPage,
        handleNextPage,
        renderPageNumbers,
    } = usePaginationHook({
        totalNumberOfData: data?.result?.total,
        perPage: query.limit,
        setCurrentPage: pageChangeHandler,
        currentPage: query.current,
        className: "m-pagination",
    });

    const resetFilter = () => {
        setFilter({
            type: {
                label: "Select Type",
                value: "",
            },
            subType: {
                label: "Select Sub Type",
                value: "",
            },
            assignees: [],
        });

        handleFirstPage();
    };

    const filterHandler = () => {
        setFilterQuery({
            ...filterQuery,
            type: filter.type.value,
            subType: filter.subType.value,
            assignees: JSON.stringify(filter.assignees.map((assignee) => assignee.value)),
        });
    };

    React.useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                filterRef.current &&
                !filterRef.current.contains(event.target as Node)
            ) {
                setShowFilter(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const subTypeOptions = (type: string) => {
        switch (type) {
            case requestTypeOptions[0].value:
                return subTypeOptionsUserLicense;
            case requestTypeOptions[1].value:
                return subTypeOptionsAssignLicense;
            case requestTypeOptions[2].value:
                return subTypeOptionsProjectTeam;
            case requestTypeOptions[3].value:
                return subTypeOptionsPermission;
            case requestTypeOptions[4].value:
                return subTypeOptionsSaaS;
            case requestTypeOptions[5].value:
                return credentialsManagementOptions;
            case requestTypeOptions[6].value:
                return subTypeUserProfileOptions;
            case requestTypeOptions[7].value:
                return subTypeOtherOptions;
            default:
                return [];
        }
    };


    return (
        <div className={pageClassName}>
            <Breadcrumb />
            <p className={`${pageClassName}__title`}>{PAGE_NAME.APPROVED_REQUEST_LIST}</p>
            <div className={`${pageClassName}__container`}>
                <div className={`${pageClassName}__header`}>
                    <div className={`${pageClassName}__search`}>
                        <Input
                            placeholder="Search"
                            onChange={searchHandler}
                            value={search}
                        />
                    </div>

                    <div className={`${pageClassName}__filterBy`}>
                        {(filterQuery.type ||
                            filterQuery.subType) && (
                                <div className={`${pageClassName}__filterBy__item`}>
                                    <p>
                                        Type:{" "}
                                        <span>
                                            {filterQuery.type === "" ? "All" : filterQuery.type}
                                        </span>
                                        {filterQuery.subType && (
                                            <span>
                                                , Sub Type:{" "}
                                                {filterQuery.subType === "" ? "All" : filterQuery.subType}
                                            </span>
                                        )}
                                    </p>
                                </div>
                            )}
                    </div>

                    <div className={`${pageClassName}__filter`} ref={filterRef}>
                        <button
                            onClick={() => setShowFilter(!isShowFilter)}
                            className={`${pageClassName}__filter__button`}
                            title="Filter"
                        >
                            <Icon icon="filter" size="primary" />
                        </button>

                        {isShowFilter && (
                            <div className={`${pageClassName}__filter__container`}>
                                <div className={`${pageClassName}__filter__item`} ref={ref}>
                                    <label>Type</label>
                                    <Dropdown
                                        options={requestTypeOptions}
                                        selected={filter.type}
                                        onClickChange={(e) =>
                                            setFilter({
                                                ...filter,
                                                type: e as { label: string; value: string },
                                                subType: { label: "Select Sub Type", value: "" },
                                            })
                                        }
                                        isBordered
                                        isIcon
                                    />
                                </div>
                                <div className={`${pageClassName}__filter__item`} ref={ref}>
                                    <label>Sub Type</label>
                                    <Dropdown
                                        options={subTypeOptions(filter.type.value)}
                                        selected={filter.subType}
                                        onClickChange={(e) =>
                                            setFilter({
                                                ...filter,
                                                subType: e as { label: string; value: string },
                                            })
                                        }
                                        isBordered
                                        isIcon
                                    />
                                </div>

                                <div className={`${pageClassName}__filter__item`}>
                                    <label htmlFor="filter">Assignees</label>
                                    <MultipleDropdown
                                        options={userListData?.result?.data.map((user: any) => ({
                                            label: user?.firstName + " " + user?.lastName,
                                            value: user?.id,
                                        }))}
                                        value={filter.assignees as { label: string; value: string }[]}
                                        dropdownHandler={(e) =>
                                            setFilter({
                                                ...filter,
                                                assignees: e as { label: string; value: string }[],
                                            })
                                        }
                                    />
                                </div>

                                <Divider />
                                <div className={`${pageClassName}__filter__btn`}>
                                    <button onClick={resetFilter}>Reset</button>
                                    <button
                                        onClick={() => {
                                            setShowFilter(false);
                                            filterHandler();
                                            handleFirstPage();
                                        }}
                                    >
                                        Apply
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                </div>
                <div className={`${pageClassName}__table`}>
                    <Table
                        header={tableHeader}
                        data={data?.result?.data ?? []}
                        loading={isLoading || isFetching || isUserListLoading}
                        firstLoad={isLoading}
                        sortHandler={sortHandler}
                        minWidth="800"
                        minHeight="330"
                        startIndex={data?.result?.from}
                        sortBy={query.sortBy}
                        viewURL={ROUTE.APPROVED_REQUEST_LIST}
                        cursorPointer
                    />
                </div>
                <Divider />
                <div className={`${pageClassName}__pagination`}>
                    <div className={`${pageClassName}__pagination__limit`}>
                        <p>Display</p>
                        <div className={`${pageClassName}__pagination__dropdown`}>
                            <Dropdown
                                options={[
                                    { label: "20", value: 20 },
                                    { label: "50", value: 50 },
                                    { label: "100", value: 100 },
                                ]}
                                selected={{ label: query.limit, value: query.limit }}
                                onClickChange={(e) => {
                                    limitChangeHandler(e.value as number);
                                    handleFirstPage();
                                }}
                                isBordered
                                isIcon

                            />
                        </div>
                    </div>
                    <div className={`${pageClassName}__pagination__number`}>
                        {data?.result?.total ? (
                            <p>
                                Show {data?.result?.from} -{" "}
                                {data?.result?.from + query.limit - 1 < data?.result?.total
                                    ? data?.result?.from + query.limit - 1
                                    : data?.result?.total}{" "}
                                of {data?.result?.total}
                            </p>
                        ) : (
                            <p>Show 00 - 00 of 00</p>
                        )}
                    </div>

                    <div className={`${pageClassName}__pagination__nav`}>
                        {data?.result?.total > query.limit && (
                            <Pagination
                                totalData={totalData}
                                page={query.current}
                                handleFirstPage={handleFirstPage}
                                handlePrevPage={handlePrevPage}
                                handleLastPage={handleLastPage}
                                handleNextPage={handleNextPage}
                                renderPageNumbers={renderPageNumbers}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ApprovedRequestList;
